import { FC } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, LucideIcon } from 'lucide-react';
import { Card } from '../../ui/Card';
import { cn } from '../../../utils/cn';

export interface ServiceCardProps {
  /**
   * Unique identifier for the service
   */
  id: string;

  /**
   * Service name
   */
  name: string;

  /**
   * Brief description of the service (1-2 sentences)
   */
  description: string;

  /**
   * Duration range (e.g., "60-90 min")
   */
  duration: string;

  /**
   * Price range (e.g., "$80-$120")
   */
  priceRange: string;

  /**
   * Icon component from lucide-react
   */
  icon: LucideIcon;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const ServiceCard: FC<ServiceCardProps> = ({
  id,
  name,
  description,
  duration,
  priceRange,
  icon: Icon,
  className,
}) => {
  return (
    <Card hoverable padding="lg" className={cn('group flex h-full flex-col', className)}>
      {/* Icon */}
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 transition-colors group-hover:bg-primary-100 dark:group-hover:bg-primary-900/70">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>

      {/* Service Name */}
      <h3 className="mb-2 font-serif text-xl font-semibold text-gray-900 dark:text-slate-100">
        {name}
      </h3>

      {/* Description */}
      <p className="mb-4 flex-grow text-gray-600 dark:text-slate-400">{description}</p>

      {/* Duration and Price */}
      <div className="mb-4 flex items-center gap-4 text-sm text-gray-500 dark:text-slate-400">
        <span className="flex items-center gap-1">
          <span className="font-medium text-gray-700 dark:text-slate-300">{duration}</span>
        </span>
        <span className="text-gray-300 dark:text-slate-600">|</span>
        <span className="font-medium text-secondary-700 dark:text-secondary-400">{priceRange}</span>
      </div>

      {/* Learn More Link */}
      <Link
        to={`/services/${id}`}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 transition-colors hover:text-primary-700 dark:hover:text-primary-300"
      >
        Learn More
        <ArrowRight
          className="h-4 w-4 transition-transform group-hover:translate-x-1"
          aria-hidden="true"
        />
      </Link>
    </Card>
  );
};

ServiceCard.displayName = 'ServiceCard';
